import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CommandBar from "../components/CommandBar";
import NotificationBell from "../components/NotificationBell";
import { APP_ROUTE_PATHS } from "../appRoutePaths";
import {
  ADMIN_NAV_ITEMS,
  CALCULATIONS_SUITE_NAV_GROUPS,
  GLOBAL_NAV_ITEMS,
  GLOBAL_RESOURCE_LINKS,
  PROJECT_SUITE_NAV_GROUPS,
  filterSuiteGroups,
  isCalculationsSuiteRoute,
  isProjectSuiteRoute,
  matchesNavItem,
} from "../data/navigationArchitecture";
import { BUILD_INFO } from "../generatedBuildInfo";
import { syncLocalPreference, USER_PREFERENCES_SYNC_EVENT } from "../utils/userPreferenceCloudStore";
import "./MainLayout.css";

const SIDEBAR_COLLAPSED_KEY = "manucalc.sidebar.collapsed";
const isBusinessAccountType = (accountType) =>
  accountType === "business" || accountType === "company";

const NavIcon = ({ name }) => (
  <span className="app-nav-icon" aria-hidden="true">
    <svg viewBox="0 0 24 24" focusable="false">
      {name === "calculator" && (
        <>
          <rect x="6" y="3" width="12" height="18" rx="2" />
          <path d="M9 7h6M9 11h1M12 11h1M15 11h1M9 15h1M12 15h1M15 15h1" />
        </>
      )}
      {name === "project" && (
        <>
          <path d="M4 6h6l2 2h8v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Z" />
          <path d="M8 13h8M8 16h5" />
        </>
      )}
      {name === "ai" && (
        <>
          <path d="M12 3l1.6 4.2L18 9l-4.4 1.8L12 15l-1.6-4.2L6 9l4.4-1.8L12 3Z" />
          <path d="M18 14l.8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8L18 14Z" />
        </>
      )}
      {name === "dashboard" && (
        <>
          <rect x="4" y="4" width="7" height="7" rx="1.5" />
          <rect x="13" y="4" width="7" height="5" rx="1.5" />
          <rect x="13" y="11" width="7" height="9" rx="1.5" />
          <rect x="4" y="13" width="7" height="7" rx="1.5" />
        </>
      )}
      {name === "menu" && (
        <>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </>
      )}
      {name === "pin" && (
        <>
          <path d="M14 4l6 6-3 1-4 4v4l-2 2-2-6-6-2 2-2h4l4-4 1-3Z" />
        </>
      )}
      {name === "history" && (
        <>
          <path d="M4 12a8 8 0 1 0 2.35-5.65L4 8.7" />
          <path d="M4 4v4.7h4.7M12 7v5l3 2" />
        </>
      )}
      {name === "settings" && (
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19 12a7.8 7.8 0 0 0-.1-1.2l2-1.5-2-3.4-2.4 1a8.7 8.7 0 0 0-2-1.2L14.2 3h-4.4l-.4 2.7a8.7 8.7 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.5A7.8 7.8 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.4-1a8.7 8.7 0 0 0 2 1.2l.4 2.7h4.4l.4-2.7a8.7 8.7 0 0 0 2-1.2l2.4 1 2-3.4-2-1.5c.1-.4.1-.8.1-1.2Z" />
        </>
      )}
      {!["calculator", "project", "ai", "dashboard", "menu", "pin", "history", "settings"].includes(name) && (
        <>
          <circle cx="12" cy="12" r="7" />
          <path d="M8.5 12h7M12 8.5v7" />
        </>
      )}
    </svg>
  </span>
);

const NavLink = ({ item, currentPath, onClick, className = "" }) => (
  <Link
    to={item.to}
    onClick={onClick}
    className={`app-nav-item ${matchesNavItem(currentPath, item) ? "active" : ""} ${className}`}
    aria-current={matchesNavItem(currentPath, item) ? "page" : undefined}
    title={item.officialName || item.label}
  >
    <NavIcon name={item.icon} />
    <span className="app-nav-label">{item.label}</span>
  </Link>
);

const NavGroup = ({ title, items, currentPath, onClick, className = "" }) => (
  <div className={`app-nav-group ${className}`}>
    {title && <div className="app-nav-group-title">{title}</div>}
    {items.map((item) => (
      <NavLink key={item.id} item={item} currentPath={currentPath} onClick={onClick} />
    ))}
  </div>
);

const TopbarActionLink = ({ to, icon, label, shortLabel }) => (
  <Link to={to} className="app-topbar-action" title={label} aria-label={label}>
    <NavIcon name={icon} />
    <span>{shortLabel || label}</span>
  </Link>
);

const MainLayout = ({
  children,
  showFooter = true,
  user,
  onLogout,
  preferences,
  accountProfile,
  isCompanyOwner = false,
  rolePermissions = {},
  isPlatformOwner = false,
}) => {
  const [commandOpen, setCommandOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [syncNotice, setSyncNotice] = useState(null);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true"
  );
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";

  const isLifetimeAdmin =
    isPlatformOwner === true ||
    accountProfile?.platformOwner === true ||
    accountProfile?.lifetimeAdminGrant === true ||
    accountProfile?.accessSource === "lifetime_admin" ||
    accountProfile?.accountBillingType === "lifetimeAdmin";
  const canAccessAdmin =
    isLifetimeAdmin ||
    (Boolean(rolePermissions?.canUseAdmin) &&
      (isCompanyOwner ||
        (isBusinessAccountType(accountProfile?.accountType) &&
          accountProfile?.role === "owner" &&
          accountProfile?.adminDashboardEnabled)));
  const canAccessJobs = rolePermissions?.canUseJobs !== false;
  const canAccessExports = rolePermissions?.canExport === true;
  const isCompanySeatAccount =
    accountProfile?.accountBillingType === "companySeat" ||
    accountProfile?.accountBillingType === "founderLifetime";
  const closeMobileSidebar = () => setMobileSidebarOpen(false);
  const isBusinessAccount = isLifetimeAdmin || isBusinessAccountType(accountProfile?.accountType);
  const projectSuiteActive = isProjectSuiteRoute(currentPath);
  const calculationsSuiteActive = isCalculationsSuiteRoute(currentPath);
  const projectSuiteGroups = filterSuiteGroups(PROJECT_SUITE_NAV_GROUPS, {
    isBusiness: isBusinessAccount,
    canAccessJobs,
    canExport: canAccessExports,
    hasCompany: Boolean(accountProfile?.companyId),
  });
  const adminItems = ADMIN_NAV_ITEMS.filter((item) => {
    if (item.platformOwner) return isPlatformOwner || isLifetimeAdmin;
    if (item.companyAdmin) return canAccessAdmin;
    return true;
  });
  const primaryNavItems = GLOBAL_NAV_ITEMS.map((item) => {
    if (item.id === "projectManagement" && projectSuiteActive) {
      return { ...item, match: [currentPath] };
    }
    if (item.id === "calculationsSuite" && calculationsSuiteActive) {
      return { ...item, match: [currentPath, "/calculator"] };
    }
    return item;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handlePreferenceSync = () => {
      setDesktopSidebarCollapsed(window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true");
    };
    window.addEventListener(USER_PREFERENCES_SYNC_EVENT, handlePreferenceSync);
    return () => window.removeEventListener(USER_PREFERENCES_SYNC_EVENT, handlePreferenceSync);
  }, []);

  const toggleDesktopSidebar = () => {
    setDesktopSidebarCollapsed((current) => {
      const next = !current;
      syncLocalPreference(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  };

  useEffect(() => {
    const handleKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen((previous) => !previous);
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const updateOnline = () => setIsOnline(navigator.onLine);
    const handleSyncStatus = (event) => {
      const detail = event.detail || {};
      if (!detail.status) return;
      setSyncNotice({
        status: detail.status,
        message: detail.message || `${detail.collectionName || detail.scope || "Workspace"} ${detail.status}`,
        at: detail.at || new Date().toISOString(),
      });
    };
    window.addEventListener("online", updateOnline);
    window.addEventListener("offline", updateOnline);
    window.addEventListener("manucalc:cloud-sync-status", handleSyncStatus);
    window.addEventListener("manucalc:user-data-sync-status", handleSyncStatus);
    return () => {
      window.removeEventListener("online", updateOnline);
      window.removeEventListener("offline", updateOnline);
      window.removeEventListener("manucalc:cloud-sync-status", handleSyncStatus);
      window.removeEventListener("manucalc:user-data-sync-status", handleSyncStatus);
    };
  }, []);

  const showSyncNotice =
    !isOnline ||
    ["error", "local", "syncing"].includes(String(syncNotice?.status || "").toLowerCase());

  return (
    <div
      className={`app-shell ${desktopSidebarCollapsed ? "sidebar-collapsed" : ""} ${
        currentPath === APP_ROUTE_PATHS.aiAssistant ? "ai-route-active" : ""
      } main-layout-dark`}
    >
      <CommandBar
        open={commandOpen}
        setOpen={setCommandOpen}
        preferences={preferences}
      />

      {mobileSidebarOpen && (
        <button
          type="button"
          className="app-sidebar-backdrop"
          aria-label="Close navigation"
          onClick={closeMobileSidebar}
        />
      )}

      <aside className={`app-sidebar ${mobileSidebarOpen ? "open" : ""}`}>
        <div className="app-brand-row">
          <Link to={APP_ROUTE_PATHS.home} className="app-brand" onClick={closeMobileSidebar}>
            <span className="app-brand-mark">MC</span>
            <span>
              <strong>ManuCalc</strong>
              <small>Manufacturing Project OS</small>
            </span>
          </Link>
          <button type="button" className="app-sidebar-collapse" onClick={toggleDesktopSidebar} title={desktopSidebarCollapsed ? "Expand navigation" : "Collapse navigation"}>
            {desktopSidebarCollapsed ? ">" : "<"}
          </button>
        </div>

        <nav className="app-sidebar-section" aria-label="Global navigation">
          <NavGroup items={primaryNavItems} currentPath={currentPath} onClick={closeMobileSidebar} />
        </nav>

        {projectSuiteActive && (
          <nav className="app-sidebar-section app-sidebar-secondary app-context-nav" aria-label="Project Management Suite navigation">
            <div className="app-context-heading">
              <span>Project Management Suite</span>
              <small>Suite tools</small>
            </div>
            {projectSuiteGroups.map((group) => (
              <NavGroup
                key={group.id}
                title={group.label}
                items={group.items}
                currentPath={currentPath}
                onClick={closeMobileSidebar}
              />
            ))}
          </nav>
        )}

        {calculationsSuiteActive && (
          <nav className="app-sidebar-section app-sidebar-secondary app-context-nav" aria-label="Calculations Suite navigation">
            <div className="app-context-heading">
              <span>Calculations Suite</span>
              <small>Calculator workspace</small>
            </div>
            {CALCULATIONS_SUITE_NAV_GROUPS.map((group) => (
              <NavGroup
                key={group.id}
                title={group.label}
                items={group.items}
                currentPath={currentPath}
                onClick={closeMobileSidebar}
              />
            ))}
          </nav>
        )}

        <nav className="app-sidebar-section app-sidebar-secondary app-sidebar-utility" aria-label="Resources and account navigation">
          <NavGroup
            title="Resources"
            items={[
              { id: "featureRequest", label: "Support & Requests", to: APP_ROUTE_PATHS.featureRequest, icon: "support" },
              { id: "onboarding", label: "Onboarding", to: APP_ROUTE_PATHS.onboarding, icon: "spark" },
              ...GLOBAL_RESOURCE_LINKS,
            ]}
            currentPath={currentPath}
            onClick={closeMobileSidebar}
          />
          {adminItems.length > 0 && (
            <NavGroup
              title="Administration"
              items={adminItems}
              currentPath={currentPath}
              onClick={closeMobileSidebar}
              className="app-admin-nav"
            />
          )}
        </nav>

        <div className="app-sidebar-footer">
          {isCompanySeatAccount && (
            <div className="app-company-pill">
              <span>Connected</span>
              <strong>{accountProfile?.companyName || "Company Workspace"}</strong>
            </div>
          )}
          {accountProfile?.isFounder && (
            <div className="app-company-pill founder">
              <span>Founding Member</span>
              <strong>{accountProfile?.founderNumber ? `#${accountProfile.founderNumber}` : "Lifetime Access"}</strong>
            </div>
          )}
          <Link to={APP_ROUTE_PATHS.settings} onClick={closeMobileSidebar} className="app-settings-pill">
            Settings
          </Link>
          {user ? (
            <button type="button" onClick={onLogout} className="app-logout-btn">
              Logout
            </button>
          ) : (
            <Link to={APP_ROUTE_PATHS.auth} onClick={closeMobileSidebar} className="app-logout-btn">
              Sign In
            </Link>
          )}
          <small title={`Build ${BUILD_INFO.buildTimestamp}`}>ManuCalc v{BUILD_INFO.version}</small>
        </div>
      </aside>

      <div className="app-main">
        <header className="app-topbar">
          <button
            type="button"
            className="app-mobile-menu"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Open navigation"
          >
            <NavIcon name="menu" />
            <span>Menu</span>
          </button>
          <button
            type="button"
            className="app-search-trigger"
            onClick={() => setCommandOpen(true)}
          >
            <span>Search projects, calculators, jobs, formulas...</span>
            <kbd>Ctrl K</kbd>
          </button>
          <div className="app-topbar-actions">
            <NotificationBell user={user} accountProfile={accountProfile} compact />
            <TopbarActionLink
              to={`${APP_ROUTE_PATHS.calculationsSuite}#pinned-calculators`}
              icon="pin"
              label="Pinned calculators"
              shortLabel="Pinned"
            />
            <TopbarActionLink
              to={`${APP_ROUTE_PATHS.calculationsSuite}#recent-tools`}
              icon="history"
              label="Recently used tools"
              shortLabel="Recent"
            />
            <TopbarActionLink
              to={APP_ROUTE_PATHS.settings}
              icon="settings"
              label="Settings"
            />
          </div>
        </header>

        {showSyncNotice && (
          <div className={`app-sync-banner ${!isOnline ? "offline" : syncNotice?.status || ""}`}>
            <strong>{!isOnline ? "Offline mode" : syncNotice?.status === "syncing" ? "Syncing" : "Sync notice"}</strong>
            <span>
              {!isOnline
                ? "You can keep working, but cloud saves, billing checks, and live collaboration may wait until the connection returns."
                : syncNotice?.message || "Cloud sync is updating."}
            </span>
          </div>
        )}

        <main className="main-content">{children}</main>

        {showFooter && <div className="app-footer-spacer" />}
      </div>
    </div>
  );
};

export default MainLayout;

